import { CloseOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import { useCallback } from "react";
import CurrencyField from "../../components/CurrencyField";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import { formatNum, parseNum } from "../../utils/numbers";
import { useUserInfo } from "../../utils/security";
import calculateTax from "./calculateTax";

const TaxItemDialog = ({ open, hide, onChange, data, disabled, idNumberLabel }) => {
    const { userInfo } = useUserInfo();
    const onSubmit = useCallback(
        (values) => {
            onChange?.(values);
            hide();
        },
        [hide, onChange]
    );
    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.taxTypeId, "taxTypeId", errors);
        validateNumberField(values.base, "base", errors);
        validateNumberField(values.total, "total", errors);
    }, []);

    const mapDataToFields = useCallback(
        ({ base, total, ...values }) => ({
            base: formatNum(base, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            total: formatNum(total, 2, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const mapFieldsToData = useCallback(
        ({ base, total, ...values }) => ({
            base: parseNum(base, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            total: parseNum(total, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            ...values,
        }),
        [userInfo]
    );
    const { values, handleChange, handleSubmit, setValues, errors } = useForm({
        data,
        onSubmit,
        validate,
        mapDataToFields,
        mapFieldsToData,
    });

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={hide}>
            <DialogTitle>Daň</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={hide}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <Box mb={2}>
                    <FormControl
                        fullWidth
                        margin="normal"
                        required
                        error={hasErrors(errors, "taxTypeId")}>
                        <InputLabel id={`tax-type-id`}>Typ daně</InputLabel>
                        <Select
                            labelId={`tax-type-id`}
                            value={values.taxTypeId || ""}
                            name="taxTypeId"
                            label="Typ daně"
                            onChange={(e) => {
                                const taxType = userInfo.taxRates.find(
                                    (taxType) => taxType.id === e.target.value
                                );
                                let calculated = calculateTax(
                                    taxType?.percentage,
                                    parseNum(
                                        values.base,
                                        userInfo.decimalDelimiter,
                                        userInfo.thousandDelimiter
                                    )
                                );
                                setValues((values) => ({
                                    ...values,
                                    percentage: taxType?.percentage,
                                    taxTypeId: taxType?.id,
                                    taxTypeName: taxType?.name,
                                    name: taxType?.name,
                                    base: formatNum(
                                        calculated.base,
                                        2,
                                        userInfo.decimalDelimiter,
                                        userInfo.thousandDelimiter
                                    ),
                                    total: formatNum(
                                        calculated.total,
                                        2,
                                        userInfo.decimalDelimiter,
                                        userInfo.thousandDelimiter
                                    ),
                                }));
                            }}>
                            {(userInfo.taxRates || []).map((option) => (
                                <MenuItem value={option.id} key={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {hasErrors(errors, "taxTypeId") && (
                            <FormFieldErrors errors={errors} field="taxTypeId" />
                        )}
                    </FormControl>
                    <CurrencyField
                        label="Základ daně"
                        name="base"
                        value={values.base || ""}
                        onChange={(e) => {
                            let calculated = calculateTax(
                                parseNum(
                                    values.percentage,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                ),
                                parseNum(
                                    e.target.value,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                )
                            );
                            setValues((values) => ({
                                ...values,
                                base: formatNum(
                                    calculated.base,
                                    2,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                ),
                                total: formatNum(
                                    calculated.total,
                                    2,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                ),
                            }));
                        }}
                        currencyCode={values.currencyCode}
                        margin="normal"
                        required
                        fullWidth
                        error={hasErrors(errors, "base")}
                        helperText={<FormFieldErrors errors={errors} field="base" />}
                    />

                    <CurrencyField
                        label="Daň celkem"
                        name="total"
                        value={values.total || ""}
                        onChange={handleChange}
                        currencyCode={values.currencyCode}
                        margin="normal"
                        required
                        fullWidth
                        error={hasErrors(errors, "total")}
                        helperText={<FormFieldErrors errors={errors} field="total" />}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={hide} disabled={disabled}>
                    Storno
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleSubmit}>
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaxItemDialog;
